import './flat-search-select-input.scss';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  Avatar,
  CircularProgress,
  FormControl,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import {isSvg} from '../../utils/functions/validate-uri-svg';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import FlatInput from '../flat-input/flat-input';
import {LoadingButton} from '@mui/lab';
import {Add} from '@mui/icons-material';

// Hold variables
const BATCH_SIZE: number = 10;

export interface SearchSelectOption<T> {
  id: string;
  name: string;
  item: T;
  avatar?: string;
}

/**
 * Select input with a search.
 *
 * Inspiration: https://liferay.design/lexicon/core-components/dropdowns/#dropdown-menu-from-plus-button
 */
export function FlatSearchSelectInput<T>(props: {
  label: string;
  handleFetchData: (
    limit: number,
    skip: number,
    search?: string
  ) => Promise<{data: Array<SearchSelectOption<T>>; total: number}>;
  handleChange: (value?: SearchSelectOption<T>) => void;
  handleBlur?: () => void;
  fetchWhenEmpty?: boolean;
  required?: boolean;
  error?: string;
  value?: SearchSelectOption<T>;
  disabled?: boolean;
  disableClear?: boolean;
  notAllowedEditing?: boolean;
  notAllowedEditingTooltipTitle?: string;
  className?: string;
  id?: string;
  avatar?: {
    type: 'user' | 'entity' | 'other';
    isIcon?: boolean;
  };
}) {
  const [options, setOptions] = useState<Array<SearchSelectOption<T>>>([]);
  const [searchTotalCount, setSearchTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [endReached, setEndReached] = useState<boolean>(false);
  const [search, setSearch] = useState<string>(props.value ? props.value.name : '');
  const {t} = useTranslation();
  const theme = useTheme();

  // Fetch data
  const handleFetchData = async (fetchMore?: boolean) => {
    try {
      // Start loading
      setLoading(true);

      // Skip
      let skip: number = 0;
      if (fetchMore === true) {
        // Set loading more
        setLoadingMore(true);

        // Set skip value
        skip = options.length;
      }

      // Get options
      const response = await props.handleFetchData(BATCH_SIZE, skip, search);

      // Total
      let total: number = 0;

      // Set options
      setOptions((prevOptions) => {
        if (fetchMore === true) {
          let result = [...prevOptions];
          for (let i = 0; i < response.data.length; i++) {
            if (!result.find((obj) => obj.id === response.data[i].id)) {
              result.push(response.data[i]);
            }
          }

          // Set total
          total = result.length;

          // Return
          return result;
        } else {
          // Set total
          total = response.data.length;

          // Return
          return [...response.data];
        }
      });
      setSearchTotalCount(response.total);

      // Set end reached
      if (total < response.total) {
        setEndReached(false);
      } else {
        setEndReached(true);
      }
    } catch (error: any) {
      // Log
      console.debug('🔴 Error while fetching data.', error);

      // Set empty values
      setOptions([]);
      setSearchTotalCount(0);
    } finally {
      // Stop loading
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Debounced get data
  useEffect(() => {
    // Timeout
    let timeout: any;

    // Search or clean
    if (search.trim().length === 0 && props.fetchWhenEmpty !== true) {
      // Set options
      setOptions([]);
      setSearchTotalCount(0);
    } else {
      // Get data after waiting 500ms
      timeout = setTimeout(() => handleFetchData(), 500);
    }

    // Clean up
    return () => {
      clearTimeout(timeout);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Handle fetch more
  const handleFetchMore = () => {
    handleFetchData(true);
  };

  // Handle option
  const handleOption = (
    value: SearchSelectOption<T> | null | string,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'clear') {
      // Clear value
      props.handleChange(undefined);

      // Update search
      setSearch('');

      // Clear options
      setOptions([]);
      setSearchTotalCount(0);
    } else {
      if (value && typeof value !== 'string') {
        // Update value
        props.handleChange(value);

        // Update search
        setSearch(value.name);
      }
    }
  };

  // Handle search
  const handleSearch = (value: string, reason: AutocompleteInputChangeReason) => {
    // Only search on user input and not when the user selected an option or clear was pressed
    if (reason === 'input') {
      // Update search
      setSearch(value);
    } else if (reason === 'clear') {
      // Update search
      setSearch('');

      // Clear options
      setOptions([]);
      setSearchTotalCount(0);
    } else if (reason === 'reset' && value.trim().length === 0) {
      // Update search
      setSearch('');

      // Clear options
      setOptions([]);
      setSearchTotalCount(0);
    }
  };

  // Handle open
  const handleOpen = () => {
    if (props.fetchWhenEmpty === true && search.trim().length === 0) {
      handleFetchData();
    }
  };

  // Render avatar
  const renderAvatar = (isIcon: boolean, option: SearchSelectOption<T>, rightMargin?: boolean) => {
    // Avatar sizes, coefficients and default icon
    const avatarSize: number = avatarSizesAndCoefficients.avatarSizes.small;
    const iconSize: number = avatarSize * avatarSizesAndCoefficients.iconSizeCoefficient;
    let defaultAvatarIcon: React.ReactElement = <></>;
    let avatarBorderRadius: string | undefined;
    if (props.avatar) {
      switch (props.avatar.type) {
        case 'user':
          defaultAvatarIcon = <PersonIcon sx={{fontSize: iconSize}} />;
          avatarBorderRadius = `${avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.circle}px`;
          break;
        case 'entity':
          defaultAvatarIcon = <BusinessIcon sx={{fontSize: iconSize}} />;
          avatarBorderRadius = `${
            avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
          }px`;
          break;
        default:
          defaultAvatarIcon = <></>;
          avatarBorderRadius = `${
            avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersHigh
          }px`;
          break;
      }
    }

    return isIcon ? (
      <Avatar
        className={`search-select-input-avatar ${
          rightMargin === true ? 'search-select-input-avatar-right-margin' : ''
        }`}
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarBorderRadius,
          borderColor: theme.palette.action.disabled,
          opacity: props.disabled === true || props.notAllowedEditing === true ? 0.38 : 1,
        }}
      >
        {option.avatar ? (
          isSvg(option.avatar) ? (
            <object
              className="search-select-input-avatar-svg-object"
              data={option.avatar}
              aria-label={option.name}
              type="image/svg+xml"
              width={`${iconSize}`}
              height={`${iconSize}`}
            ></object>
          ) : (
            <img
              style={{
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
              src={option.avatar}
              alt={option.name}
            />
          )
        ) : (
          defaultAvatarIcon
        )}
      </Avatar>
    ) : (
      <Avatar
        className={`search-select-input-avatar ${
          rightMargin === true ? 'search-select-input-avatar-right-margin' : ''
        }`}
        src={option.avatar}
        alt={option.name}
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarBorderRadius,
          borderColor: theme.palette.action.disabled,
          opacity: props.disabled === true || props.notAllowedEditing === true ? 0.38 : 1,
        }}
      >
        {defaultAvatarIcon}
      </Avatar>
    );
  };

  return (
    <FormControl className={`flat-search-select-input ${props.disabled ? 'disabled' : ''}`}>
      <Autocomplete
        id={props.id}
        //className={props.className}
        options={options}
        // @ts-ignore
        getOptionLabel={(option: SearchSelectOption<T>) => option.name}
        value={props.value || null}
        isOptionEqualToValue={(option: SearchSelectOption<T>, value: SearchSelectOption<T>) =>
          option.id === value.id
        }
        onChange={(
          event: React.SyntheticEvent,
          value: SearchSelectOption<T> | null | string,
          reason: AutocompleteChangeReason
        ) => handleOption(value, reason)}
        onInputChange={(
          event: React.SyntheticEvent,
          value: string,
          reason: AutocompleteInputChangeReason
        ) => handleSearch(value, reason)}
        onBlur={props.handleBlur}
        onOpen={handleOpen}
        loading={loading}
        freeSolo={props.fetchWhenEmpty === true ? false : true}
        disableClearable={props.disableClear === true ? true : false}
        disabled={props.disabled === true ? true : false}
        renderInput={(params) => (
          <FlatInput
            {...params}
            label={props.label}
            required={props.required === true ? true : false}
            InputLabelProps={{
              ...params.InputLabelProps,
              children: null,
              shrink: true,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment
                    position="start"
                    sx={{
                      justifySelf: 'center',
                      height: 'auto',
                      marginTop: '0!important',
                    }}
                  >
                    <SearchIcon />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                  {props.avatar &&
                    props.value &&
                    renderAvatar(props.avatar.isIcon === true ? true : false, props.value)}
                </>
              ),
            }}
            error={props.error ? true : false}
            helperText={props.error || undefined}
          />
        )}
        renderOption={(renderProps, option) => {
          return (
            <Fragment key={`${option.name}-${option.id}`}>
              <li
                {...renderProps}
                style={{
                  flexDirection: 'column',
                }}
              >
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  sx={{width: '100%', padding: 0, margin: 0}}
                >
                  {props.avatar &&
                    renderAvatar(props.avatar.isIcon === true ? true : false, option, true)}
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      marginLeft: '10px',
                    }}
                  >
                    {option.name}
                  </Typography>
                </Stack>
              </li>
              {
                option.id === options[options.length - 1].id && (
                  <li style={{flexDirection: 'column'}}>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      gap="10px"
                      sx={{mt: '10px', padding: '10px'}}
                    >
                      <LoadingButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        endIcon={loadingMore ? <CircularProgress size={16} /> : <Add />}
                        loadingPosition={loadingMore ? 'start' : undefined}
                        disabled={endReached || loadingMore}
                        onClick={handleFetchMore}
                      >
                        {t(translations.dynamic_form.feature_fields.autocomplete.load_more)}
                      </LoadingButton>
                      <Typography fontSize="12px">
                        {t(translations.dynamic_form.feature_fields.autocomplete.counter, {
                          current: options.length,
                          max: searchTotalCount,
                        })}
                      </Typography>
                    </Stack>
                  </li>
                )
                /* (loadingMore ? (
                    <Box className="search-select-input-footer-container">
                      <Typography color="GrayText" variant="body1">
                        {t(translations.search_select_input.loading)}
                      </Typography>
                    </Box>
                  ) : !endReached ? (
                    <Box className="search-select-input-footer-container">
                      <Typography color="GrayText" variant="body1">
                        {t(translations.search_select_input.items_showing_information, {
                          value: options.length,
                          total: searchTotalCount,
                        })}
                      </Typography>
                      <Box className="search-select-input-footer-more-button-container">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={handleFetchMore}
                          endIcon={<AddIcon />}
                        >
                          {t(translations.search_select_input.more_button)}
                        </Button>
                      </Box>
                    </Box>
                  ) : undefined) */
              }
            </Fragment>
          );
        }}
        loadingText={t(translations.search_select_input.loading)}
        noOptionsText={t(translations.search_select_input.no_options)}
      />
    </FormControl>
  );
}
